import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const API_URL = 'blog-post/';

export interface CategoriaPublicacion {
    id: number;
    nombre: string;
    slug: string;
    descripcion: string | null;
    creado_en: string;
}

export interface Publicacion {
    id: number;
    titulo: string;
    slug: string;
    subtitulo: string | null;
    contenido: string;
    imagen: string | null;
    url_imagen: string | null;
    categoria: CategoriaPublicacion | null;
    categoria_id?: number | null; // solo para escritura
    autor: number;
    nombre_autor: string;
    es_destacado: boolean;
    estado: 'borrador' | 'publicado';
    fecha_publicacion: string | null;
    creado_en: string;
    actualizado_en: string;
}

export type PublicacionData = {
    titulo: string;
    subtitulo?: string | null;
    contenido: string;
    imagen?: File | null; // para subir imagen
    categoria_id?: number | null;
    es_destacado?: boolean;
    estado?: 'borrador' | 'publicado';
    fecha_publicacion?: string | null;
};

// GET: Obtener todas las publicaciones (con filtros vía query params)
export const fetchPublicaciones = async (params?: {
    estado?: 'borrador' | 'publicado';
    es_destacado?: boolean;
    categoria__slug?: string;
    search?: string;
    ordering?: string;
}): Promise<Publicacion[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Publicacion[]>(API_URL, { params });
        return response.data;
    });
};

// GET: Obtener una publicación por slug
export const fetchPublicacion = async (slug: string): Promise<Publicacion> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Publicacion>(`${API_URL}${slug}/`);
        return response.data;
    });
};

// GET: Obtener publicaciones destacadas (solo publicadas)
export const fetchDestacados = async (): Promise<Publicacion[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Publicacion[]>(`${API_URL}destacados/`);
        return response.data;
    });
};

// GET: Obtener publicaciones por slug de categoría (solo publicadas)
export const fetchPorCategoria = async (slugCategoria: string): Promise<Publicacion[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Publicacion[]>(`${API_URL}por-categoria/${slugCategoria}/`);
        return response.data;
    });
};

// POST: Crear una nueva publicación (con o sin imagen)
export const createPublicacion = async (data: PublicacionData) => {
    return handleRequestWithAuth(async () => {
        const formData = new FormData();
        formData.append('titulo', data.titulo);
        if (data.subtitulo) formData.append('subtitulo', data.subtitulo);
        formData.append('contenido', data.contenido);
        if (data.categoria_id) formData.append('categoria_id', data.categoria_id.toString());
        if (data.es_destacado !== undefined) formData.append('es_destacado', String(data.es_destacado));
        if (data.estado) formData.append('estado', data.estado);
        if (data.fecha_publicacion) formData.append('fecha_publicacion', data.fecha_publicacion);
        if (data.imagen) formData.append('imagen', data.imagen);

        const response = await axiosInstance.post(API_URL, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    });
};

// PUT: Actualizar una publicación existente por slug
export const updatePublicacion = async (slug: string, data: Partial<PublicacionData>) => {
    return handleRequestWithAuth(async () => {
        const formData = new FormData();
        if (data.titulo !== undefined) formData.append('titulo', data.titulo);
        if (data.subtitulo !== undefined) formData.append('subtitulo', data.subtitulo || '');
        if (data.contenido !== undefined) formData.append('contenido', data.contenido);
        if (data.categoria_id !== undefined) formData.append('categoria_id', data.categoria_id?.toString() || '');
        if (data.es_destacado !== undefined) formData.append('es_destacado', String(data.es_destacado));
        if (data.estado !== undefined) formData.append('estado', data.estado);
        if (data.fecha_publicacion !== undefined) formData.append('fecha_publicacion', data.fecha_publicacion || '');
        if (data.imagen !== undefined) {
            if (data.imagen) formData.append('imagen', data.imagen);
            else formData.append('imagen', ''); // para eliminar la imagen existente
        }

        const response = await axiosInstance.put(`${API_URL}${slug}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    });
};

// DELETE: Eliminar una publicación por slug o id
export const deletePublicacion = async (slugOrId: string | number) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.delete(`${API_URL}${slugOrId}/`);
        return response.data;
    });
};